/* eslint-disable camelcase */
import {
  Button,
  Collapse,
  CreateButton,
  EditButton,
  List,
  ShowButton,
  Space,
  Table,
  Typography,
  useDrawerForm,
  useModal
} from '@pankod/refine-antd'
import { useTranslate } from '@pankod/refine-core'
import { CreateMarketOrder } from 'components/market_order/create'
import { definitions } from 'interfaces'
import { currency } from 'utility/currency'

import { ButtonLogicDelete } from 'components/DeleteModal/buttonLogicDelete'
import { DeleteModal } from 'components/DeleteModal/modal'
import { ExtraInfoMarkeplaces } from 'components/ExtraInfoMarkeplaces/ExtraInfoMarkeplaces'
import { EditMarketOrder } from 'components/market_order/edit'
import SellerFilter from 'components/market_order/sellerFilter'
import { useEffect, useState } from 'react'
import { PaymentPurchaseMethodId } from 'types/types'
import { ADVISE_INTRA_STATUS, ADVISE_INTRA_STATUS_PARTIAL, ADVISE_IVA_STATUS, ADVISE_IVA_STATUS_PARTIAL, AMAZON_MARKETPLACE_ID, AMAZON_SELLER_FEATURE, CUSTOM_ORDER_MARKETPLACE_ID, ECONOMY_SHIPMENT_METHOD_ID, extractMainDomainFromURL, formatDateTime, getGroupedMarketplace, getNewSellers, getSignedUrl, prepareUrl, REDUCED_SHIPMENT_METHOD_ID, sendEmailRequest, supabaseClient } from 'utility'
import { PAYMENT_PURCHASE_METHOD_MAPPER } from 'utility/mapper'
import { openToast } from 'utility/notification'
import '../../custom.css'

type MarketOrdersProps = {
  order: definitions['Order'] | undefined;
};

type newSellerType = {
  id: number,
  seller: string | null,
  order_id?: {
    id: number,
    shipping_method_id: number,
    status_id: number
  },
  supplier_info_id?: {
    id: number,
    description: string,
  }
  verified?: boolean,
  seller_info?: string
}

export const MarketOrders: React.FC<MarketOrdersProps> = (props) => {
  const t = useTranslate()

  let callingServer = false

  const order = props?.order

  const { Panel } = Collapse

  const { modalProps, show, close } = useModal()
  const [id, setId] = useState(0)
  const [customShopData, setCustomShopData] = useState<any>(null)
  const [retrieveShop, setRetrieveShop] = useState<boolean>(true)
  const [relatedMarketOrders, setRelatedMarketOrder] = useState<any[]>([])

  const [marketplaceShipmentPrice, setMarketplaceShipmentPrice] = useState<number>(0)
  const [sellersData, setSellerData] = useState<{ sellers: newSellerType[], marketplaces: newSellerType[] } | null>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [tableProps, setTableProps] = useState<any>({ dataSource: [] })

  const [haveSaving, setHaveSaving] = useState(true)
  const [loading, setLoading] = useState(false)

  const retrieveMarketOrders = async () => {
    if (!callingServer) {
      callingServer = true
      const { data: marketData } = await supabaseClient.from('market_orders').select('*')
        .eq('order_id', order?.id)
        .eq('deleted', false)
        .order('id', { ascending: true })

      const tableProps = { dataSource: marketData }

      setTableProps(tableProps)
    }
    callingServer = false
  }

  const retrieveSellers = async () => {
    if (order?.shipping_method_id === ECONOMY_SHIPMENT_METHOD_ID) setSellerData(null)
    else {
      const { data: amazonVar } = await supabaseClient.from<definitions['variables']>('variables')
        .select('id, name, value')
        .eq('name', AMAZON_SELLER_FEATURE)
        .single()

      if (!(amazonVar?.value === 'true') && order?.marketplace_id.id === AMAZON_MARKETPLACE_ID) return
      const sellerList: string[] = []
      const marketplacesList: string[] = []
      if (order?.items) {
        order?.items?.forEach((i) => (i.seller) ? sellerList.push(i.seller) : null)
        order?.items?.forEach((i) => {
          if (i.custom_order_site && order.marketplace_id.id === CUSTOM_ORDER_MARKETPLACE_ID) {
            const urlData = extractMainDomainFromURL(i.url)
            marketplacesList.push(urlData.domainWithoutSuffix || new URL(i.url).hostname)
          }
        })
        if (sellerList.length === 0 && order.marketplace_id.id !== 0) {
          setSellerData(null)
        } else {
          setSellerData((await getNewSellers(sellerList, marketplacesList, order.id) || []) as unknown as { sellers: newSellerType[], marketplaces: newSellerType[] })
        }
      }
    }
  }

  useEffect(() => {
    // console.log('sorter -->', sorter)
    if (!callingServer) {
      if (tableProps.dataSource.length > 0) {
        retrieveMarketOrders()
      }
    }
  }, [currentPage])

  useEffect(() => {
    retrieveMarketOrders()
    if (order?.shipping_method_id !== ECONOMY_SHIPMENT_METHOD_ID) {
      retrieveSellers()
    }
  }, [])

  const {
    drawerProps: createDrawerProps,
    formProps: createFormProps,
    show: createShow,
    close: createClose
  } = useDrawerForm<definitions['market_orders']>({
    action: 'create',
    resource: 'market_orders',
    redirect: false
  })

  const {
    formProps: editFormProps,
    drawerProps: editDrawerProps,
    show: editShow,
    close: editClose
  } = useDrawerForm<definitions['market_orders']>({
    action: 'edit',
    resource: 'market_orders',
    redirect: false
  })

  createFormProps.initialValues = { order_id: order?.id }

  const extraInfo = (typeof order?.marketplace_id?.extra_info === 'string') ? JSON.parse(order?.marketplace_id?.extra_info) : order?.marketplace_id?.extra_info
  const marketId = order?.marketplace_id?.id || 0
  const subtotal = order?.subtotal || 0

  useEffect(() => {
    const refundTransportSupplements = order?.supplements
      .filter(s => !s.volumetric && !s.deleted && (s.description === 'refundTransport' || s.description === 'transportToPeninsula' || s.description === 'transportSupplement'))
      .map(s => s.supplement || 0)
      .reduce((a, b) => a + b, 0) || 0

    setMarketplaceShipmentPrice((order?.marketplace_id.shipment_price || 0) + (refundTransportSupplements))
    setHaveSaving(!!(order && order.total_saving > 0 && order.iva_status === null))
  }, [order])

  const expectedMarketOrderPrice = subtotal

  const retrieveCustomShopInfo = async () => {
    let customUrl: string | null = null

    order?.items?.forEach((i: any) => {
      if (customUrl === null && i.url.startsWith('http')) {
        customUrl = extractMainDomainFromURL(i.url).domainWithoutSuffix || prepareUrl(i.url)
      }
    })

    if (customUrl && customUrl !== '') {
      const res = await getGroupedMarketplace(customUrl)
      if (res.internal || res.related) {
        setCustomShopData(res)
      }
    }
    setRetrieveShop(false)
  }

  if (marketId === 0 && retrieveShop) {
    retrieveCustomShopInfo()
  }

  const customShopHaveInfo = () => {
    if ((customShopData?.internal && typeof customShopData?.internal === 'object') ||
      (customShopData?.internal && customShopData?.internal[0] && customShopData?.internal[0].extra_info)) {
      // (customShopData?.related && customShopData?.related[0] && customShopData?.related[0].extra_info)) {
      return true
    } else {
      return false
    }
  }

  const backgroundColorStyle = ((extraInfo !== '' && extraInfo !== null && extraInfo?.info && extraInfo?.info.length > 0) || customShopHaveInfo()) ? '#f5f5cb' : 'transparent'

  const sendEmail = async (type: string) => {
    setLoading(true)
    const onSuccess = () => {
      openToast('info', {
        placement: 'bottomRight',
        message: 'INFO',
        description: t('market_order.fields.emailSent')
      })
      setLoading(false)
    }

    const onError = () => {
      openToast('error', {
        placement: 'bottomRight',
        message: 'ERROR',
        description: t('errors.sendEmailError')
      })
      setLoading(false)
    }

    const record = {
      msgType: type,
      orderId: order!.id,
      userId: order!.user_id
    }

    let ivaStatus = (type === 'intra') ? ADVISE_INTRA_STATUS : ADVISE_IVA_STATUS
    let ivaIntra = type
    if (order?.items) {
      const itemsWithSeller = order?.items?.filter((i) => !!i.seller && i.seller.toLowerCase() !== 'seller error')
      // If is a order without sellers or just with one seller, set the states
      if (itemsWithSeller.length > 1) {
        ivaStatus = (type === 'intra') ? ADVISE_INTRA_STATUS_PARTIAL : ADVISE_IVA_STATUS_PARTIAL
        ivaIntra = (type === 'intra') ? 'intra_partial' : 'iva_partial'
      }
    }

    await sendEmailRequest(record, 'market_orders', onSuccess, onError)
    await supabaseClient.from('orders')
      .update({
        iva_status: ivaStatus,
        iva_intra: ivaIntra,
        updated_by: supabaseClient.auth.user()?.email
      })
      .eq('id', order?.id)
  }

  const showSellerAdvise = () => {
    const haveSeller = order?.items?.some(i => i.seller) || false

    const newSellerOnList: (string | undefined)[] = order?.items?.filter(i => i.seller && !sellersData?.sellers.map(s => s.seller?.toLowerCase()).includes(i.seller.toLowerCase().replace(/\n/g, '')))?.map(i => i.seller?.replace(/\n/g, '')) || []

    // console.log({ haveSeller, newSellerOnList, sellersData, items: order?.items })

    if (order?.shipping_method_id === ECONOMY_SHIPMENT_METHOD_ID || order?.shipping_method_id === REDUCED_SHIPMENT_METHOD_ID) return null
    else {
      if (haveSeller) {
        return <div>
          {(newSellerOnList.length > 0) ? <b style={{ backgroundColor: 'orange' }}>{t('common.new_seller')} {`-> ${newSellerOnList}`}</b> : null}

          {sellersData?.sellers.map(s => {
            return <p key={s.id}>{s.seller} -- {s.supplier_info_id?.description}</p>
          })}
        </div>
      } else {
        return <div>
          {sellersData?.marketplaces.length === 0 ? <b style={{ backgroundColor: 'orange' }}>{t('common.new_seller')}</b> : null}
        </div>
      }
    }
  }

  return (
    <List key={(order?.marketplace_id.shipment_price || 0) + (order?.supplements.length || 0)}
      title={`${t('market_order.market_orders')} - ${t('market_order.expectedPrice')}: ${currency(expectedMarketOrderPrice)} / ${t('market_order.marketplaceShipmentPrice')}: ${currency(marketplaceShipmentPrice)}`}
      breadcrumb={false}
    >
      <div style={{ marginTop: -50, float: 'right', display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
        <Button key={`intra-${order?.id}-button`} loading={loading} disabled={!haveSaving} onClick={async () => await sendEmail('intra')}>
          {t('market_order.fields.sendINTRAEmail')}
        </Button>
        <Button key={`iva-${order?.id}-button`} loading={loading} disabled={!haveSaving} onClick={async () => await sendEmail('iva')}>
          {t('market_order.fields.sendIVAEmail')}
        </Button>
        <SellerFilter />
        <CreateButton onClick={() => createShow()} >
          {t('order.actions.addMarketOrder')}
        </CreateButton>
      </div>

      <Space direction="vertical" style={{ width: '100%', height: '100%', right: '0px' }}>
        {sellersData && showSellerAdvise()}
      </Space>

      {(order?.promotional_code_market !== null)
        ? <div style={{ backgroundColor: 'khaki', borderRadius: '5px' }}><Typography style={{ margin: '0px 10px 20px 10px' }} >{t('market_order.fields.promotionalCodeMarket')} <b>{order?.promotional_code_market}</b></Typography></div>
        : null}

      <Collapse key={`${marketId}${customShopData?.internal?.length}${customShopData?.related?.length}`} style={{ backgroundColor: `${backgroundColorStyle}`, marginBottom: '15px' }} bordered={false} ghost={false} defaultActiveKey={['1']}>
        <Panel style={{ backgroundColor: 'transparent' }} header={t('market_order.fields.extraInfo')} key="1">
          {(!customShopData) ? <ExtraInfoMarkeplaces extraInfo={extraInfo} id={marketId} /> : <ExtraInfoMarkeplaces customShopData={customShopData} id={marketId} />}
        </Panel>
      </Collapse>

      <Table key={`marketOrder${tableProps?.dataSource?.length || 0}`} {...tableProps}
        rowClassName={(record) => (record.reviewed) ? 'locked-resource' : ''}
        pagination={{
          current: currentPage,
          pageSize: 4,
          total: tableProps?.dataSource?.length || 0,
          onChange (page, pageSize) {
            setCurrentPage(page)
          }
        }}
        rowKey="id">

        <Table.Column
          key='id'
          dataIndex='id'
          title={t('purchases.fields.id') as string}
          width={100}
          align='center'
          sorter={{
            compare: (a: any, b: any) => {
              const aId = Number(a.id) || 0
              const bId = Number(b.id) || 0
              return aId - bId
            }
          }}
        />
        <Table.Column
          key="purchase_id"
          dataIndex="purchase_id"
          title={t('market_order.fields.purchaseId')}
          align='center'
          sorter
        />
        <Table.Column
          key="transaction_id"
          dataIndex="transaction_id"
          title={t('market_order.fields.transactionId')}
          align='center'
          sorter
        />
        <Table.Column
          key="purchase_amount"
          dataIndex="purchase_amount"
          title={t('market_order.fields.purchaseAmount')}
          render={(value) => currency(value)}
          align='center'
          sorter
        />
        <Table.Column
          key="iva_type"
          dataIndex="iva_type"
          title={'Tipo de IVA'}
          align='center'
          sorter
        />
        <Table.Column
          key="iva_amount"
          dataIndex="iva_amount"
          title={'IVA (€)'}
          align='center'
          sorter
        />
        <Table.Column
          key="tracking_id"
          dataIndex="tracking_id"
          title={t('market_order.fields.trackingId')}
          align='center'
          sorter
        />
        <Table.Column
          key="invoice_number"
          dataIndex="invoice_number"
          title={t('market_order.fields.invoiceNumber')}
          align='center'
          sorter
        />
        <Table.Column
          key="invoice_date"
          dataIndex="invoice_date"
          title={t('market_order.fields.invoiceDate')}
          align='center'
          render={(value) => value ? formatDateTime(value, true) : ''}
          sorter
        />
        <Table.Column
          key="file_url"
          dataIndex="file_url"
          align='center'
          title={t('market_order.fields.invoiceFile')}
          render={(value, record: any) => {
            if (value && record?.purchase_parent) {
              return <ShowButton onClick={async () => {
                const url = await getSignedUrl(value)
                if (url) window.open(url, '_blank', 'noopener,noreferrer')
                else window.open(value, '_blank', 'noopener,noreferrer')
              }}></ShowButton>
            } else {
              const parent = tableProps.dataSource.find((x: any) => x.purchase_id === record.purchase_id && x.file_url !== null && x.invoice_number === record.invoice_number)
              if (parent && record?.is_purchase === true) return <>{t('market_order.fields.shared')}</>
              else return null
            }
          }}
        />
        <Table.Column
          key="is_purchase"
          dataIndex="is_purchase"
          title={t('market_order.fields.type')}
          align='center'
          render={(value) => value ? t('market_order.fields.purchase') : t('market_order.fields.refund')}
          sorter
        />
        <Table.Column
          key="payment_method_id"
          dataIndex="payment_method_id"
          title={t('invoices_matchings.fields.payment_method_id')}
          align='center'
          render={(value) => t(`invoices_matchings.fields.payment_type.${PAYMENT_PURCHASE_METHOD_MAPPER[value as PaymentPurchaseMethodId]}`)}
          sorter
        />
        <Table.Column<definitions['market_orders']>
            title={t('table.actions')}
            dataIndex="actions"
            render={(_, record) => {
              if (record.purchase_parent) {
                return <Space>
                  <EditButton
                    onClick={() => {
                      editShow(record.id)
                      let array = []
                      if (record.is_purchase) {
                        array = tableProps.dataSource?.filter((mo: any) => mo.purchase_id === record.purchase_id && mo.is_purchase === true && mo.invoice_number === record.invoice_number) || []
                      } else {
                        if (record.invoice_number !== '' && record.invoice_number !== null) {
                          array = tableProps.dataSource?.filter((mo: any) => mo.purchase_id === record.purchase_id && mo.is_purchase === false && mo.invoice_number === record.invoice_number) || []
                        } else {
                          array = tableProps.dataSource?.filter((mo: any) => mo.purchase_id === record.purchase_id && mo.is_purchase === false) || []
                        }
                      }

                      setRelatedMarketOrder(array)
                    }}
                    hideText
                  />
                  <ButtonLogicDelete
                    disabled={record.reviewed}
                    click={() => {
                      show()
                      setId(record.id)
                      let array = []
                      if (record.is_purchase) {
                        if (record.invoice_number !== '' && record.invoice_number !== null) {
                          array = tableProps.dataSource?.filter((mo: any) => mo.purchase_id === record.purchase_id && mo.is_purchase === true && mo.invoice_number === record.invoice_number) || []
                        } else {
                          array = tableProps.dataSource?.filter((mo: any) => mo.purchase_id === record.purchase_id && mo.is_purchase === true) || []
                        }
                      } else {
                        if (record.invoice_number !== '' && record.invoice_number !== null) {
                          array = tableProps.dataSource?.filter((mo: any) => mo.purchase_id === record.purchase_id && mo.is_purchase === false && mo.invoice_number === record.invoice_number) || []
                        } else {
                          array = tableProps.dataSource?.filter((mo: any) => mo.purchase_id === record.purchase_id && mo.is_purchase === false) || []
                        }
                      }
                      setRelatedMarketOrder(array)
                    }}
                  />
                  <DeleteModal
                    modalProps={modalProps}
                    close={close}
                    id={id}
                    relatedMarketOrders={relatedMarketOrders}
                    entity='market_orders'
                    tableQueryResult={retrieveMarketOrders}
                  />
                </Space>
              } else return null
            }}
          />
      </Table>
      <CreateMarketOrder
        drawerProps={createDrawerProps}
        formProps={createFormProps}
        tableQueryResult={retrieveMarketOrders}
        closeDrawer={createClose}
        defaultMarketplace={order?.marketplace_id}
        order={order}
        expectedMarketOrderPrice={expectedMarketOrderPrice}
        marketplaceShipmentPrice={marketplaceShipmentPrice}
      />
      <EditMarketOrder
        drawerProps={editDrawerProps}
        formProps={editFormProps}
        tableQueryResult={retrieveMarketOrders}
        closeDrawer={editClose}
        relatedPurchases={relatedMarketOrders}
        defaultMarketplace={order?.marketplace_id}
        order={order}
        expectedMarketOrderPrice={expectedMarketOrderPrice}
        marketplaceShipmentPrice={marketplaceShipmentPrice}
      />
    </List>
  )
}
